/** Helpers for Trading Dash overview (fixture rail + market tiles). */

import { getOrderBookDepthRefreshPolicy } from '../../utils/orderBookDepthRefreshPolicy';
import { previewFixedMarginDecimal, previewFixedMarginImplied, quoteMarginationClamps } from '../../utils/fixedMarginPreview';
import {
  batchSuspendButtonLabel,
  findActiveBatchSuspension,
  sportBucketForFixture,
} from '../../utils/batchSuspensionUtils';

export { batchSuspendButtonLabel, findActiveBatchSuspension, sportBucketForFixture };

export function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function sameCalendarDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function fixtureStartDate(fixture) {
  const raw = fixture?.start_time_utc;
  if (!raw) return null;
  const d = typeof raw === 'number' ? new Date(raw * 1000) : new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatFixtureTime(fixture) {
  const d = fixtureStartDate(fixture);
  if (!d) return '—';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function formatFixtureDateLabel(d) {
  if (!d) return '—';
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatShortDate(d) {
  if (!d) return '—';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm} / ${dd} / ${yyyy}`;
}

/** ``YYYY-MM-DD`` for ``<input type="date">`` / select values (local calendar day). */
export function dateToInputValue(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Parse ``YYYY-MM-DD`` as local start-of-day. */
export function dateFromInputValue(s) {
  const raw = String(s || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split('-').map(Number);
  if (!y || !m || !d) return null;
  return startOfDay(new Date(y, m - 1, d));
}

export function fixtureMatchup(fixture) {
  const away = fixture?.away_team_abbr || abbrev(fixture?.away_team);
  const home = fixture?.home_team_abbr || abbrev(fixture?.home_team);
  if (away && home) return `${away} @ ${home}`;
  return fixture?.game_id || 'Fixture';
}

/** Full team names for fixture rail and headers. */
export function fixtureFullName(fixture) {
  const away = String(fixture?.away_team || '').trim();
  const home = String(fixture?.home_team || '').trim();
  if (away && home) return `${away} @ ${home}`;
  if (away || home) return away || home;
  return fixture?.game_id || 'Fixture';
}

export function abbrev(name) {
  return String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((p) => p[0]?.toUpperCase())
    .join('') || '?';
}

export function marketLabel(id) {
  return String(id || 'Market')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export function formatProb(p) {
  if (p == null || !Number.isFinite(Number(p))) return '';
  return `.${(Number(p) * 1000).toFixed(0).padStart(3, '0').slice(-3)}`;
}

/** Fair P display — same as order book `fmtPct`. */
export function formatFairPct(p) {
  if (p == null || !Number.isFinite(Number(p))) return '—';
  return `${(Number(p) * 100).toFixed(2)}%`;
}

function quoteNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Posted marginated decimal odds (live quote, else if-gated preview). */
export function quotePostedDecimals(quote) {
  const over =
    quoteNum(quote?.marginated_price) ?? quoteNum(quote?.marginated_price_if_gated);
  const under =
    quoteNum(quote?.marginated_price_under) ?? quoteNum(quote?.marginated_price_under_if_gated);
  return {
    over: over != null && over > 0 ? over : null,
    under: under != null && under > 0 ? under : null,
  };
}

/** Additive over margin at quoted decimal: (1 / dec) − P(over fair). */
export function quoteTrueMarginOver(quote) {
  const { over: dec } = quotePostedDecimals(quote);
  const pFair = quoteNum(quote?.p_fair);
  if (dec == null || pFair == null) return null;
  return 1 / dec - pFair;
}

/** Additive under margin at quoted decimal: (1 / dec) − P(under fair). */
export function quoteTrueMarginUnder(quote) {
  const { under: dec } = quotePostedDecimals(quote);
  const pFair =
    quoteNum(quote?.p_fair_under)
    ?? (quoteNum(quote?.p_fair) != null ? 1 - quoteNum(quote.p_fair) : null);
  if (dec == null || pFair == null) return null;
  return 1 / dec - pFair;
}

export function formatMarginPp(m) {
  if (m == null || !Number.isFinite(Number(m))) return '—';
  const pp = Number(m) * 100;
  const sign = pp >= 0 ? '+' : '';
  return `${sign}${pp.toFixed(1)}pp`;
}

export function formatTrueMargin(quote) {
  return formatMarginPp(quoteTrueMarginOver(quote));
}

export function formatTrueMarginUnder(quote) {
  return formatMarginPp(quoteTrueMarginUnder(quote));
}

export function formatDecimalPrice(x) {
  if (x == null || !Number.isFinite(Number(x)) || Number(x) <= 0) return '—';
  return Number(x).toFixed(2);
}

export function quoteTotalFees(quote) {
  const v = quote?.entry_stats?.total_fees;
  return Number.isFinite(Number(v)) ? Number(v) : 0;
}

export function quoteEntryCount(quote) {
  const v = quote?.entry_stats?.n_entries;
  return Number.isFinite(Number(v)) ? Number(v) : 0;
}

/** Latched book-movement z² (KF / competitor batch); null when zero or missing. */
export function quoteZ2Innovation(quote) {
  const z = Number(quote?.last_z_innovation);
  if (!Number.isFinite(z) || z <= 0) return null;
  return z;
}

export function formatZ2Innovation(z) {
  if (z == null || !Number.isFinite(Number(z))) return '';
  return Number(z).toFixed(3);
}

/** Visual heat for z² — muted gray → amber → dark orange (``max`` ≈ typical gate threshold). */
export function z2InnovationHeatStyle(z, max = 0.75) {
  const v = Number(z);
  if (!Number.isFinite(v) || v <= 0) return null;
  const t = Math.min(1, Math.max(0, v / max));
  const hue = 215 + (22 - 215) * t;
  const sat = 12 + (88 - 12) * t;
  const light = 58 + (42 - 58) * t;
  const bgAlpha = 0.04 + t * 0.32;
  return {
    color: `hsl(${hue.toFixed(1)}, ${sat.toFixed(1)}%, ${light.toFixed(1)}%)`,
    backgroundColor: `hsla(22, ${(65 + t * 25).toFixed(0)}%, ${(38 - t * 6).toFixed(0)}%, ${bgAlpha.toFixed(3)})`,
    fontWeight: t >= 0.85 ? 700 : t >= 0.45 ? 600 : 500,
  };
}

export function formatFees(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
}

export function sumQuoteFees(quotes) {
  return (quotes || []).reduce((sum, q) => sum + quoteTotalFees(q), 0);
}

export const POPULARITY_FILTER_OPTIONS = [
  { value: 'all', label: 'All activity' },
  { value: 'has_entries', label: 'Has entries' },
  { value: 'fees_50', label: 'Fees ≥ $50' },
  { value: 'fees_200', label: 'Fees ≥ $200' },
];

export const BOARD_SORT_OPTIONS = [
  { value: 'player', label: 'Player (A→Z)' },
  { value: 'popularity', label: 'Popularity (fees)' },
  { value: 'innovation', label: 'Book movement (z²)' },
  { value: 'market_type', label: 'Market type' },
];

export function passesPopularityFilter(quote, filter) {
  if (!filter || filter === 'all') return true;
  const fees = quoteTotalFees(quote);
  const n = quoteEntryCount(quote);
  if (filter === 'has_entries') return n > 0 || fees > 0;
  if (filter === 'fees_50') return fees >= 50;
  if (filter === 'fees_200') return fees >= 200;
  return true;
}

export function marketOptionsFromQuotes(quotes) {
  const set = new Set();
  for (const q of quotes || []) {
    if (q.market_id) set.add(q.market_id);
  }
  return [...set].sort((a, b) => marketLabel(a).localeCompare(marketLabel(b)));
}

function sortTiles(tiles, sortBy) {
  const rows = [...tiles];
  if (sortBy === 'innovation') {
    return rows.sort(
      (a, b) =>
        (quoteZ2Innovation(b) ?? 0) - (quoteZ2Innovation(a) ?? 0)
        || String(a.market_id).localeCompare(String(b.market_id))
        || Number(a.line) - Number(b.line)
    );
  }
  if (sortBy === 'popularity') {
    return rows.sort(
      (a, b) =>
        quoteTotalFees(b) - quoteTotalFees(a)
        || quoteEntryCount(b) - quoteEntryCount(a)
        || String(a.market_id).localeCompare(String(b.market_id))
        || Number(a.line) - Number(b.line)
    );
  }
  if (sortBy === 'market_type') {
    return rows.sort(
      (a, b) =>
        String(a.market_id).localeCompare(String(b.market_id))
        || Number(a.line) - Number(b.line)
        || String(a.player_name || '').localeCompare(String(b.player_name || ''))
    );
  }
  return rows.sort(
    (a, b) =>
      String(a.market_id).localeCompare(String(b.market_id))
      || Number(a.line) - Number(b.line)
  );
}

function sortPlayers(players, sortBy) {
  const rows = [...players];
  if (sortBy === 'innovation') {
    return rows.sort((a, b) => {
      const maxZ = (p) => Math.max(0, ...p.tiles.map((t) => quoteZ2Innovation(t) ?? 0));
      return maxZ(b) - maxZ(a) || a.name.localeCompare(b.name);
    });
  }
  if (sortBy === 'popularity') {
    return rows.sort(
      (a, b) =>
        sumQuoteFees(b.tiles) - sumQuoteFees(a.tiles)
        || b.tiles.reduce((s, t) => s + quoteEntryCount(t), 0)
          - a.tiles.reduce((s, t) => s + quoteEntryCount(t), 0)
        || a.name.localeCompare(b.name)
    );
  }
  if (sortBy === 'market_type') {
    return rows.sort((a, b) => {
      const am = a.tiles[0]?.market_id || '';
      const bm = b.tiles[0]?.market_id || '';
      return String(am).localeCompare(String(bm)) || a.name.localeCompare(b.name);
    });
  }
  return rows.sort((a, b) => a.name.localeCompare(b.name));
}

export function isOneWay(quote) {
  if (!quote) return false;
  const ps = quote.gate_breakdown?.product_shape;
  const tob = quote.gate_breakdown?.soft_engine?.tob_margin;
  if (ps?.tob_force_one_sided === true || tob?.force_one_sided === true) return true;
  // Suspended/gated quotes drop under prices; use CI/product shape, not missing under.
  if (!quote.gate_allowed || quote.manual_suspend) {
    const { isProductEffectivelyTwoSided } = getOrderBookDepthRefreshPolicy(quote);
    return !isProductEffectivelyTwoSided;
  }
  if (quote.quote_two_sided === false) return true;
  const u = quote.marginated_price_under;
  return u == null || !Number.isFinite(Number(u)) || Number(u) <= 0;
}

/** Persisted manual 1w/2w override, if any (API fields or gate_breakdown flags). */
export function quoteExplicitOneWay(quote) {
  if (quote?.force_one_sided === true) return true;
  if (quote?.force_two_sided === true) return false;
  const flags = quote?.gate_breakdown?.manual_intervention_flags;
  if (flags?.one_sided_waives_soft_engine) return true;
  if (flags?.two_sided_waives_soft_engine) return false;
  return null;
}

export function quoteCommittedOneWay(quote) {
  const explicit = quoteExplicitOneWay(quote);
  if (explicit !== null) return explicit;
  return isOneWay(quote);
}

const TIER1_SHAPE_PERSIST_BLOCKERS = new Set([
  'tier1_pre_game',
  'tier1_min_books',
  'tier1_derived_parents',
  'tier1_fixture_suspended',
  'tier1_live_feed',
  'tier1_opta_live',
  'tier1_kalshi_live',
  'lineups_rotowire',
  'lineups_manual_lineup',
]);

/** Blocked quote with no persisted force_* yet — confirm 1w/2w to leave-open + shape (Actions parity). */
export function needsShapePersistOverride(quote) {
  if (!quote || quote.gate_allowed) return false;
  if (quoteExplicitOneWay(quote) !== null) return false;
  const gb = quote.gate_breakdown || {};
  if (gb.liquidity_refresh?.blocked_entirely) return false;
  const dominant = gb.dominant_blocker;
  if (dominant && TIER1_SHAPE_PERSIST_BLOCKERS.has(dominant)) return false;
  return true;
}

export function quoteManualOverrideKinds(quote) {
  const kinds = [];
  if (quote?.manual_suspend) kinds.push('suspended');
  else if (quote?.manual_shaping_active) kinds.push('shaping');
  else if (quote?.is_manually_gated) kinds.push('gated');
  if (quote?.mute_competitor_signals) kinds.push('competitor mute');
  if (quote?.manual_alts_allow != null) kinds.push('alts policy');
  if (quote?.manual_prob_margin_over != null || quote?.manual_prob_margin_under != null) {
    kinds.push('fixed margin');
  }
  return kinds;
}

export function quoteHasManualOverride(quote) {
  return quoteManualOverrideKinds(quote).length > 0;
}

export function deriveTileStatus(quote, { batchSuspended = false } = {}) {
  if (quote?.manual_suspend) return 'yellow';
  if (batchSuspended) return 'batch';
  if (!quote?.gate_allowed) return 'red';
  if (quote?.manual_shaping_active || (quote?.n_books != null && quote.n_books < 3)) return 'grey';
  return 'green';
}

export function fixtureIsLive(fixture) {
  if (!fixture) return false;
  if (fixture.is_live) return true;
  const st = String(fixture.status || '').toLowerCase();
  return st === 'live' || st === 'in_play' || st === 'in progress';
}

export function fixtureStatusLabel(fixture) {
  if (fixtureIsLive(fixture)) return 'LIVE';
  const st = String(fixture?.status || '').toLowerCase();
  if (st === 'complete' || st === 'closed') return 'DONE';
  return formatFixtureTime(fixture);
}

export function normalizeActiveFixture(row) {
  if (!row) return null;
  return {
    game_id: row.fixture_id || row.game_id,
    home_team: row.home_team,
    away_team: row.away_team,
    home_team_abbr: row.home_team_abbr,
    away_team_abbr: row.away_team_abbr,
    start_time_utc: row.start_time_utc,
    status: row.status,
    league: row.league,
    sport: row.sport,
    total_engines: row.total_engines,
    hours_until_start: row.hours_until_start,
    is_live: row.hours_until_start != null && row.hours_until_start < 0 && row.status !== 'complete',
  };
}

export function filterFixtures(fixtures, { selectedDate, sport, league, search, includeInGame = true }) {
  let rows = fixtures || [];
  if (selectedDate) {
    rows = rows.filter((f) => {
      const d = fixtureStartDate(f);
      return d && sameCalendarDay(d, selectedDate);
    });
  }
  if (!includeInGame) {
    rows = rows.filter((f) => !fixtureIsLive(f));
  }
  if (sport && sport !== 'all') {
    const want = sport.toLowerCase();
    rows = rows.filter((f) => sportBucketForFixture(f) === want);
  }
  if (league && league !== 'all') {
    rows = rows.filter((f) => leaguesMatch(f.league, league));
  }
  const q = String(search || '').trim().toLowerCase();
  if (q) {
    rows = rows.filter((f) => {
      const blob = [
        f.home_team,
        f.away_team,
        f.home_team_abbr,
        f.away_team_abbr,
        f.league,
        f.game_id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }
  return [...rows].sort((a, b) => {
    const ta = fixtureStartDate(a)?.getTime() ?? 0;
    const tb = fixtureStartDate(b)?.getTime() ?? 0;
    return ta - tb;
  });
}

export function filterActiveFixtures(rows, { selectedDate, sport, league, search, includeInGame = true }) {
  const fixtures = (rows || []).map(normalizeActiveFixture).filter(Boolean);
  return filterFixtures(fixtures, { selectedDate, sport, league, search, includeInGame });
}

/** Canonical league key for filter/compare (Optic slug, display name, Sleeper key). */
export function normalizeLeagueKey(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Sleeper ↔ Optic pairs (subset of backend LEAGUE_DICT). */
const LEAGUE_ALIAS_PAIRS = [
  ['worldcup', 'fifa_-_world_cup'],
  ['epl', 'england_-_premier_league'],
  ['uefachamps', 'uefa_-_champions_league'],
  ['bundesliga', 'germany_-_bundesliga'],
  ['seriea', 'italy_-_serie_a'],
  ['ligue1', 'france_-_ligue_1'],
  ['laliga', 'spain_-_la_liga'],
  ['mls', 'usa_-_major_league_soccer'],
  ['facup', 'england_-_fa_cup'],
  ['cbb', 'ncaab'],
  ['cfb', 'ncaaf'],
];

export function leagueMatchKeys(raw) {
  const primary = normalizeLeagueKey(raw);
  if (!primary) return new Set();
  const keys = new Set([primary]);
  for (const [a, b] of LEAGUE_ALIAS_PAIRS) {
    const na = normalizeLeagueKey(a);
    const nb = normalizeLeagueKey(b);
    if (primary === na || primary === nb) {
      keys.add(na);
      keys.add(nb);
    }
  }
  return keys;
}

export function leaguesMatch(a, b) {
  if (!a || !b) return false;
  const ka = leagueMatchKeys(a);
  const kb = leagueMatchKeys(b);
  for (const key of ka) {
    if (kb.has(key)) return true;
  }
  return false;
}

export function leagueOptionsFromFixtures(fixtures) {
  const set = new Set();
  for (const f of fixtures || []) {
    const lg = String(f.league || '').trim();
    if (lg) set.add(lg);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

export function sportOptionsFromFixtures(fixtures) {
  const set = new Set();
  for (const f of fixtures || []) {
    const bucket = sportBucketForFixture(f);
    if (bucket) set.add(bucket);
  }
  return [...set].sort();
}

/** Min fee-weighted sharp for "sharp fees only" recent-entry filter. */
export const SHARP_FEES_MIN = 0.5;

export function recentEntryFixtureId(marketKey) {
  return String(marketKey || '').split(':')[0] || '';
}

function passesRecentPopularity(row, filter) {
  if (!filter || filter === 'all') return true;
  const fees = Number(row.total_fees) || 0;
  const n = Number(row.n_entries) || 0;
  if (filter === 'has_entries') return n > 0 || fees > 0;
  if (filter === 'fees_50') return fees >= 50;
  if (filter === 'fees_200') return fees >= 200;
  return true;
}

/** Recently-entered rows: league + player only (not fixture/date/board filters). */
export function filterRecentEntries(
  rows,
  {
    fixtureIds,
    league = 'all',
    search = '',
    player = '',
    marketType = 'all',
    popularity = 'all',
    sharpFeesOnly = false,
  } = {}
) {
  const q = String(search || '').trim().toLowerCase();
  const playerQ = String(player || '').trim().toLowerCase();
  const idSet =
    fixtureIds instanceof Set ? fixtureIds : new Set(Array.isArray(fixtureIds) ? fixtureIds : []);

  return (rows || []).filter((row) => {
    if (idSet.size > 0) {
      const fid = recentEntryFixtureId(row.market_key);
      if (!idSet.has(fid)) return false;
    }

    if (league && league !== 'all') {
      if (!leaguesMatch(row.league, league)) return false;
    }
    if (playerQ) {
      const pn = String(row.player_name || '').toLowerCase();
      if (pn !== playerQ && !pn.includes(playerQ)) return false;
    }
    if (marketType && marketType !== 'all' && row.market_id !== marketType) return false;
    if (!passesRecentPopularity(row, popularity)) return false;
    if (sharpFeesOnly) {
      const sharp = row.avg_fee_weighted_sharp_rate;
      if (sharp == null || !Number.isFinite(Number(sharp)) || Number(sharp) < SHARP_FEES_MIN) {
        return false;
      }
    }
    if (!q) return true;
    const blob = [
      row.player_name,
      row.team_name,
      row.market_id,
      row.market_key,
      row.league,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return blob.includes(q);
  });
}

export function groupQuotesForBoard(
  quotes,
  fixture,
  {
    search = '',
    player = '',
    marketType = 'all',
    popularity = 'all',
    sortBy = 'player',
  } = {}
) {
  const q = String(search || '').trim().toLowerCase();
  const playerQ = String(player || '').trim().toLowerCase();
  const filtered = (quotes || []).filter((row) => {
    if (playerQ) {
      const pn = String(row.player_name || row.player_id || '').toLowerCase();
      if (pn !== playerQ && !pn.includes(playerQ)) return false;
    }
    if (marketType && marketType !== 'all' && row.market_id !== marketType) return false;
    if (!passesPopularityFilter(row, popularity)) return false;
    if (!q) return true;
    const blob = [row.player_name, row.team_name, row.market_id, row.market_key, row.league]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return blob.includes(q);
  });

  const homeName = fixture?.home_team || '';
  const awayName = fixture?.away_team || '';
  const homeAbbr = fixture?.home_team_abbr || abbrev(homeName);
  const awayAbbr = fixture?.away_team_abbr || abbrev(awayName);

  const players = new Map();
  const gameLineEntities = new Map();
  for (const quote of filtered) {
    const isGameLine = Boolean(quote?.game_line)
      || String(quote?.player_id || '').trim().toLowerCase() === 'game';
    const bucket = isGameLine ? gameLineEntities : players;
    const playerId = quote.player_id || quote.player_name || 'player';
    if (!bucket.has(playerId)) {
      bucket.set(playerId, {
        id: playerId,
        name:
          isGameLine && String(playerId).toLowerCase() === 'game'
            ? 'Game'
            : quote.player_name || playerId,
        pos: quote.league || '—',
        side: null,
        tiles: [],
      });
    }
    const row = bucket.get(playerId);
    row.tiles.push(quote);
    if (!isGameLine) {
      const side = String(quote?.player_team_side || '').trim().toLowerCase();
      if (side === 'home' || side === 'away') {
        row.side = side;
      }
    }
  }

  const playerList = sortPlayers(
    [...players.values()].map((p) => ({
      ...p,
      tiles: sortTiles(p.tiles, sortBy),
      totalFees: sumQuoteFees(p.tiles),
      totalEntries: p.tiles.reduce((s, t) => s + quoteEntryCount(t), 0),
    })),
    sortBy
  );

  const gameLineList = sortPlayers(
    [...gameLineEntities.values()].map((p) => ({
      ...p,
      tiles: sortTiles(p.tiles, sortBy),
      totalFees: sumQuoteFees(p.tiles),
      totalEntries: p.tiles.reduce((s, t) => s + quoteEntryCount(t), 0),
    })),
    sortBy
  );

  const awayPlayers = playerList.filter((p) => p.side === 'away');
  const homePlayers = playerList.filter((p) => p.side === 'home');
  const unknownPlayers = playerList.filter((p) => p.side !== 'home' && p.side !== 'away');

  const teams = [];
  if (gameLineList.length) {
    teams.push({
      code: 'GL',
      name: 'Game lines',
      side: 'game_lines',
      players: gameLineList,
    });
  }
  teams.push(
    {
      code: awayAbbr,
      name: awayName || 'Away',
      side: 'away',
      players: awayPlayers,
    },
    {
      code: homeAbbr,
      name: homeName || 'Home',
      side: 'home',
      players: homePlayers,
    }
  );

  if (unknownPlayers.length) {
    teams.push({
      code: '?',
      name: 'Unassigned team',
      side: 'unknown',
      players: unknownPlayers,
    });
  }

  if (!playerList.length && !gameLineList.length) return [];

  return teams;
}

function marginStrFromQuoteValue(v) {
  if (v == null || !Number.isFinite(Number(v))) return '';
  return String(Number(v));
}

export function marginStringsFromQuote(quote) {
  return {
    over: marginStrFromQuoteValue(quote?.manual_prob_margin_over),
    under: marginStrFromQuoteValue(quote?.manual_prob_margin_under),
  };
}

function marginDraftEqual(a, b) {
  return String(a ?? '').trim() === String(b ?? '').trim();
}

export function marginDraftPending(draft) {
  if (!draft) return false;
  return (
    !marginDraftEqual(draft.targetMarginOver, draft.committedMarginOver)
    || !marginDraftEqual(draft.targetMarginUnder, draft.committedMarginUnder)
  );
}

export function quoteFairUnder(quote) {
  return quoteNum(quote?.p_fair_under)
    ?? (quoteNum(quote?.p_fair) != null ? 1 - quoteNum(quote.p_fair) : null);
}

/** Preview decimal at line when a fixed Δp override is staged (blank = auto → null). */
export function marginPreviewDecimal(quote, side, marginStr, oneway) {
  const s = String(marginStr ?? '').trim();
  if (s === '') return null;
  const m = Number.parseFloat(s);
  if (!Number.isFinite(m) || m < 0 || m > 0.5) return null;
  if (side === 'under' && oneway) return null;
  const pFair = side === 'over' ? quoteNum(quote?.p_fair) : quoteFairUnder(quote);
  if (pFair == null) return null;
  const { floor, ceiling } = quoteMarginationClamps(quote);
  return previewFixedMarginDecimal(pFair, m, floor, ceiling);
}

/** Effective applied Δp when a fixed override is staged (after floor/ceiling clamp). */
export function stagedFixedMarginEffective(quote, side, marginStr, oneway) {
  const s = String(marginStr ?? '').trim();
  if (s === '') return null;
  const m = Number.parseFloat(s);
  if (!Number.isFinite(m)) return null;
  if (side === 'under' && oneway) return null;
  const pFair = side === 'over' ? quoteNum(quote?.p_fair) : quoteFairUnder(quote);
  if (pFair == null) return null;
  const { floor, ceiling } = quoteMarginationClamps(quote);
  const implied = previewFixedMarginImplied(pFair, m, floor, ceiling);
  if (implied == null) return null;
  const pc = Math.max(1e-6, Math.min(1 - 1e-6, pFair));
  return implied - pc;
}

function marginFieldPayload(targetStr) {
  const s = String(targetStr ?? '').trim();
  if (s === '') return null;
  const v = Number.parseFloat(s);
  return Number.isFinite(v) ? v : undefined;
}

/** Gate payload for one pending tile (1w/2w, fixed Δp). Suspend uses batch publish rules, not manual gate. */
export function buildDraftGatePayload(draft) {
  const payload = {};

  const onewayChange = draft.targetOneway !== draft.committedOneway;
  const shapeApply = Boolean(draft.shapePersist) || onewayChange;
  if (shapeApply) {
    payload.leave_open = true;
    if (draft.targetOneway) payload.force_one_sided = true;
    else payload.force_two_sided = true;
  }

  if (marginDraftPending(draft)) {
    payload.leave_open = true;
    if (!marginDraftEqual(draft.targetMarginOver, draft.committedMarginOver)) {
      payload.manual_prob_margin_over = marginFieldPayload(draft.targetMarginOver);
    }
    if (!marginDraftEqual(draft.targetMarginUnder, draft.committedMarginUnder)) {
      payload.manual_prob_margin_under = marginFieldPayload(draft.targetMarginUnder);
    }
  }

  return payload;
}

/**
 * Stable key so pending tiles with identical gate shapes can share one bulk-gate call.
 * Distinguishes unset margin fields from explicit null (clear).
 */
export function bulkGateShapeKey(payload) {
  const over = Object.prototype.hasOwnProperty.call(payload, 'manual_prob_margin_over')
    ? payload.manual_prob_margin_over
    : '__unset__';
  const under = Object.prototype.hasOwnProperty.call(payload, 'manual_prob_margin_under')
    ? payload.manual_prob_margin_under
    : '__unset__';
  return JSON.stringify([
    payload.leave_open ?? null,
    payload.force_one_sided ?? null,
    payload.force_two_sided ?? null,
    over,
    under,
  ]);
}

/**
 * Group pending [marketKey, draft] entries into bulk-gate batches (same shape → one request).
 * @returns {{ payload: object, market_keys: string[] }[]}
 */
export function groupPendingDraftsForBulkGate(pendingEntries) {
  const groups = new Map();
  for (const [marketKey, draft] of pendingEntries) {
    const payload = buildDraftGatePayload(draft);
    const key = bulkGateShapeKey(payload);
    let group = groups.get(key);
    if (!group) {
      group = { payload, market_keys: [] };
      groups.set(key, group);
    }
    group.market_keys.push(marketKey);
  }
  return [...groups.values()];
}

export function emptyTileDraft(quote) {
  const margins = marginStringsFromQuote(quote);
  const committedOneway = quoteCommittedOneWay(quote);
  return {
    marketKey: quote.market_key,
    committedOneway,
    targetOneway: committedOneway,
    shapePersist: false,
    committedMarginOver: margins.over,
    targetMarginOver: margins.over,
    committedMarginUnder: margins.under,
    targetMarginUnder: margins.under,
  };
}

export function tileIsPending(draft) {
  if (!draft) return false;
  return (
    draft.targetOneway !== draft.committedOneway
    || Boolean(draft.shapePersist)
    || marginDraftPending(draft)
  );
}
